// Session-based analytics adapter to preserve existing API while writing to Firestore sessions
import { sessionAnalytics } from '../services/analytics/sessionAnalytics.service';


type GenericMetadata = Record<string, any>;

// Compatibility layer: expose a simple trackEvent that delegates to session analytics
export const trackEvent = (
  event: string,
  userId: string,
  metadata: GenericMetadata = {},
  questionnaireId?: string
) => {
  // Map legacy events to core session types and include legacy data
  const map: Record<string, { type: 'page_view' | 'form_edit' | 'form_save' | 'form_submit' | 'error'; }>
    = {
      questionnaire_started: { type: 'page_view' },
      field_updated: { type: 'form_edit' },
      section_changed: { type: 'form_edit' },
      auto_save_completed: { type: 'form_save' },
      bulk_import_used: { type: 'form_edit' },
      share_link_created: { type: 'form_save' },
      questionnaire_completed: { type: 'form_submit' },
    };

  const mapped = map[event]?.type || 'form_edit';
  void sessionAnalytics.track(
    // userId preserved via ensureSession using useAuth below if available; we accept explicit userId for compatibility
    // We'll pass a minimal user object when possible via a lazy getter from AuthContext
    (globalThis as any).__voluntas_current_user__ || ({ uid: userId } as any),
    mapped,
    {
      pageId: questionnaireId,
      data: { legacyEvent: event, questionnaireId, ...metadata },
    }
  );
};

// Legacy functions preserved; all delegate to trackEvent
export const getStoredEvents = (): any[] => {
  // No longer stored locally; return empty array to preserve type compatibility
  return [];
};

export const getQuestionnaireEvents = (_questionnaireId: string): any[] => {
  return [];
};

export const calculateQuestionnaireAnalytics = (_questionnaireId: string) => {
  return {
    totalEvents: 0,
    timeSpent: 0,
    fieldUpdates: 0,
    sectionChanges: 0,
    autoSaves: 0,
    lastActivity: new Date(),
    completionProgress: 0,
  } as any;
};

export const cleanupOldEvents = () => {};

export const trackQuestionnaireStarted = (questionnaireId: string, userId: string, businessType: string) => {
  trackEvent('questionnaire_started', userId, { businessType }, questionnaireId);
};

export const trackFieldUpdated = (questionnaireId: string, userId: string, questionId: string, hasValue: boolean) => {
  trackEvent('field_updated', userId, { questionId, hasValue }, questionnaireId);
};

export const trackSectionChanged = (questionnaireId: string, userId: string, fromSection: string, toSection: string) => {
  trackEvent('section_changed', userId, { fromSection, toSection }, questionnaireId);
};

export const trackAutoSave = (questionnaireId: string, userId: string, success: boolean, duration: number) => {
  trackEvent('auto_save_completed', userId, { success, duration }, questionnaireId);
};

export const trackBulkImport = (questionnaireId: string, userId: string, fieldsImported: number) => {
  trackEvent('bulk_import_used', userId, { fieldsImported }, questionnaireId);
};

export const trackShareLinkCreated = (questionnaireId: string, userId: string, permissions: string) => {
  trackEvent('share_link_created', userId, { permissions }, questionnaireId);
};

export const trackQuestionnaireCompleted = (questionnaireId: string, userId: string, finalScore: number) => {
  trackEvent('questionnaire_completed', userId, { finalScore }, questionnaireId);
};

// Optional: page view helper for general pages
export const trackPageView = async (pageId: string, user?: { uid: string }) => {
  const currentUser = (globalThis as any).__voluntas_current_user__ || user || null;
  await sessionAnalytics.track(currentUser as any, 'page_view', { pageId });
};

// Expose a small helper to register current user to be used by analytics adapter
export const registerCurrentUserForAnalytics = (user: any | null) => {
  (globalThis as any).__voluntas_current_user__ = user || null;
};

const analyticsService = {
  trackEvent,
  getStoredEvents,
  getQuestionnaireEvents,
  calculateQuestionnaireAnalytics,
  cleanupOldEvents,
  trackQuestionnaireStarted,
  trackFieldUpdated,
  trackSectionChanged,
  trackAutoSave,
  trackBulkImport,
  trackShareLinkCreated,
  trackQuestionnaireCompleted,
  trackPageView,
  registerCurrentUserForAnalytics,
};

export default analyticsService;